import { type PasswordConfig } from '../../../shared/types';
import { baseUrl } from '../../../shared/utils';

type LoginAPIResponse =
  | { success: false }
  | {
      success: true;
      expired: false | undefined;
      access_token: string;
      refresh_token: string;
      expires_in: number;
      token_type: 'Bearer';
    }
  | {
      success: true;
      expired: true;
      one_time_token: string;
      password_policy?: {
        min_length?: number;
        max_length?: number;
        min_uppercase_chars?: number;
        min_lowercase_chars?: number;
        min_alpha_chars?: number;
        min_numeric_chars?: number;
        min_alphanumeric?: number;
        min_special_chars?: number;
        min_unique_chars?: number;
        max_repeat_chars?: number;
        regex?: string;
        regex_description?: string;
      };
    };

type LoginResponse =
  | {
      isPasswordExpired: true;
      oneTimeToken: string;
      passwordValidationConfig: PasswordConfig;
    }
  | {
      isPasswordExpired: false;
    };

/** Calls the login API and returns a LoginResponse or throws an exception */
export async function login({
  username,
  password,
}: {
  username: string;
  password: string;
}): Promise<LoginResponse> {
  const response = await fetch(`${baseUrl.authms}/v1/auth/web-login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password,
    }),
  });

  if (response.status !== 200) {
    throw new Error();
  }

  const loginResponse = (await response.json()) as LoginAPIResponse;

  if (!loginResponse.success) {
    throw new Error();
  }

  if (loginResponse.expired) {
    const policy = loginResponse.password_policy;

    if (!policy) {
      return {
        isPasswordExpired: true,
        oneTimeToken: loginResponse.one_time_token,
        passwordValidationConfig: {},
      };
    }

    return {
      isPasswordExpired: true,
      oneTimeToken: loginResponse.one_time_token,
      passwordValidationConfig: {
        minLength: policy.min_length,
        maxLength: policy.max_length,
        minUpperCase: policy.min_uppercase_chars,
        minLowerCase: policy.min_lowercase_chars,
        minAlpha: policy.min_alpha_chars,
        minNumeric: policy.min_numeric_chars,
        minAlphaneumeric: policy.min_alphanumeric,
        minSpecial: policy.min_special_chars,
        minUnique: policy.min_unique_chars,
        maxRepeats: policy.max_repeat_chars,
        regex: policy.regex,
        regexDescription: policy.regex_description,
      },
    };
  }

  return {
    isPasswordExpired: false,
  };
}

interface IdpUrlResponse {
  redirectUrl: string;
}

export type IdpUrlProps = { username: string } | { alias: string };

/**
 * Fetches a list of provides, will filter with a username
 * @param username - Used to filter providers
 */
export async function getIdpRedirectUrl(props: IdpUrlProps): Promise<string> {
  const response = await fetch(
    `${baseUrl.authms}/v1/sso/identity-providers/initiate-login`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        'alias' in props
          ? { idpName: props.alias }
          : { username: props.username }
      ),
    }
  );

  if (response.status !== 200) {
    // Surface the error message from the API response (e.g. IDP_NOT_FOUND)
    let errorMessage = '';

    try {
      const body = (await response.json()) as { message?: string };

      errorMessage = body.message ?? '';
    } catch {
      // Response body may not be JSON; fall through with empty message
    }

    throw new Error(errorMessage);
  }

  const { redirectUrl } = (await response.json()) as IdpUrlResponse;

  return redirectUrl;
}

interface MfaStatus {
  requiresRegistration: boolean;
  enabled: boolean;
  loginVerified: boolean;
}

/** Gets current MFA status of user and environment. */
export async function getMfaStatus(): Promise<MfaStatus> {
  const response = await fetch(`${baseUrl.authms}/mfa`);

  if (!response.ok) {
    throw new Error('Error getting MFA status');
  }

  return (await response.json()) as MfaStatus;
}

interface LoginPageInfo {
  welcomeMessage: string;
  version: string;
}

/** Gets login page info from BE. */
export async function getLoginPageInfo(): Promise<LoginPageInfo> {
  const response = await fetch(`${baseUrl.root}/ECM/login/pageinfo`);

  if (!response.ok) {
    throw new Error();
  }

  const { welcomeMessage, version } =
    (await response.json()) as Partial<LoginPageInfo>;

  return {
    welcomeMessage: welcomeMessage ?? '',
    version: version ?? '',
  };
}
